import { useState, useEffect, useRef } from "react";
import { api } from "../../../lib/api";
import toast from "react-hot-toast";

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: "New flight" },
  { id: 2, label: "Get fee" },
  { id: 3, label: "Tell customer" },
  { id: 4, label: "Payment link" },
  { id: 5, label: "Customer pays" },
  { id: 6, label: "Change flight" },
];

// True once the customer has paid the reschedule fee, whether through an
// agent-sent link or the chatbot's own link (an escalation ticket may carry the
// chatbot payment copied onto reschedule_payment_status / _razorpay_payment_id /
// _paid_at). Read defensively: any one of them means paid, so an already-paid
// escalation goes straight to Confirm instead of Get Fee / Payment again.
export function isReschedulePaid(ticket) {
  if (!ticket) return false;
  if (String(ticket.reschedule_payment_status || "").trim().toLowerCase() === "paid") return true;
  if (ticket.reschedule_razorpay_payment_id && String(ticket.reschedule_razorpay_payment_id).trim() !== "") return true;
  if (ticket.reschedule_paid_at) return true;
  return false;
}

export function deriveActiveStep(ticket) {
  if (!ticket) return 1;
  if (ticket.status === "Closed") return 7;
  if (isReschedulePaid(ticket)) return 6;
  if (ticket.reschedule_payment_link_id) return 5;
  if (Number(ticket.tbo_verified) === 1) return 4;
  if (
    ticket.reschedule_fee != null &&
    String(ticket.reschedule_fee).trim() !== "" &&
    Number.isFinite(Number(ticket.reschedule_fee))
  )
    return 3;
  // Offline reissue: Step 1 until the customer has chosen one of the agent's flights.
  if (isOfflineReissueTicket(ticket) && !ticket.reschedule_details_parsed?.agreed_option) return 1;
  if (ticket.reschedule_details_parsed) return 2;
  return 1;
}

function defaultFormatINR(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDateTime(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  } catch {
    return dt;
  }
}

// Converts any date string / ISO to "YYYY-MM-DDTHH:MM" for datetime-local inputs.
// Date-only strings (YYYY-MM-DD) are treated as local midnight so the date is pre-filled correctly.
function toDateTimeLocal(val) {
  if (!val) return "";
  try {
    const s = String(val).trim();
    // Date-only: avoid UTC parse which shifts the date in IST
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

// Splits a combined TBO flight string like "AI 101" or "6E 237" into
// { code: "AI", number: "101" }. Falls back gracefully when format is unexpected.
function parseFlightToken(flightNumber) {
  if (!flightNumber) return { code: "", number: "" };
  const s = String(flightNumber).trim();
  const spaceIdx = s.indexOf(" ");
  if (spaceIdx === -1) return { code: "", number: s };
  const maybeCode = s.slice(0, spaceIdx);
  if (maybeCode.length >= 2 && maybeCode.length <= 3) {
    return { code: maybeCode, number: s.slice(spaceIdx + 1) };
  }
  return { code: "", number: s };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Round trip handled as one request: both flights on this one ticket
// (server: reschedule.service.js makeTripTicket, reschedule_details.automation.legs).
function TripLegsList({ ticket }) {
  const legs = ticket?.reschedule_details_parsed?.automation?.legs;
  if (!legs || typeof legs !== "object") return null;
  const rows = Object.values(legs).sort((a, b) => (a?.leg === "outbound" ? -1 : b?.leg === "outbound" ? 1 : 0));
  const statusText = (st) => (st === "completed" ? "Changed" : "In progress");
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 space-y-1" data-testid="trip-legs">
      <div className="font-semibold text-gray-600">Round trip: one request for both flights</div>
      {rows.map((l, i) => (
        <div key={i} className="flex justify-between gap-3">
          <span>
            <span className="font-semibold">{l?.leg === "return" ? "Return" : "Outbound"}:</span> {l?.flight || "—"}
            {l?.new_booking_id ? ` · new TBO BookingId ${l.new_booking_id}` : ""}
          </span>
          <span className={l?.status === "completed" ? "text-green-700 font-semibold" : "text-blue-700"}>{statusText(l?.status)}</span>
        </div>
      ))}
    </div>
  );
}

function StepTracker({ activeStep }) {
  const done = activeStep >= 7;
  return (
    <div className="flex items-start w-full mb-6">
      {STEPS.map((step, i) => {
        const complete = done || step.id < activeStep;
        const active = step.id === activeStep && !done;
        const isLast = i === STEPS.length - 1;
        return (
          <div key={step.id} className="flex-1 flex flex-col items-center relative">
            {i > 0 && (
              <div
                className={`absolute top-3.5 h-0.5 transition-colors ${
                  complete || active ? "bg-blue-400" : "bg-gray-200"
                }`}
                style={{ left: 0, right: "50%", transform: "translateX(-50%)", width: "100%" }}
              />
            )}
            {!isLast && (
              <div
                className={`absolute top-3.5 h-0.5 transition-colors ${
                  complete ? "bg-blue-400" : "bg-gray-200"
                }`}
                style={{ left: "50%", width: "50%" }}
              />
            )}
            <div
              className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                complete
                  ? "bg-blue-500 text-white"
                  : active
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-400 border border-gray-300"
              }`}
            >
              {complete ? "✓" : step.id}
            </div>
            <span
              className={`mt-1.5 text-[10px] font-semibold text-center leading-tight whitespace-nowrap ${
                active ? "text-blue-700" : complete ? "text-blue-600" : "text-gray-400"
              }`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StepCard({ stepNum, activeStep, title, icon, children }) {
  const done = activeStep > stepNum;
  const active = activeStep === stepNum;
  const locked = activeStep < stepNum;

  if (done) {
    return (
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-2.5 flex items-center gap-2">
        <span className="text-blue-500 font-bold text-sm">✓</span>
        <span className="text-sm text-blue-700 font-medium">{title}</span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-all ${
        locked
          ? "opacity-50 border-gray-200 bg-white"
          : active
          ? "border-blue-300 bg-white shadow-lg"
          : "border-gray-200 bg-white"
      }`}
    >
      <div
        className={`px-4 py-3 border-b flex items-center gap-2 ${
          active ? "bg-blue-600" : "bg-gray-50"
        }`}
      >
        {icon && <span className="text-sm">{icon}</span>}
        <span
          className={`text-sm font-semibold ${active ? "text-white" : "text-gray-700"}`}
        >
          {title}
        </span>
        {locked && (
          <span className="ml-auto text-xs text-gray-400">
            Finish the step above first
          </span>
        )}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function FlightComparisonCard({ ticket, bookings, formatINR }) {
  const details = ticket?.reschedule_details_parsed;
  if (!details) return null;

  const allBookings = [
    ...(bookings?.upcoming || []),
    ...(bookings?.current || []),
  ];
  const currentBooking = allBookings[0];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Current flight
          </div>
          {currentBooking ? (
            <>
              <div className="text-sm font-semibold">
                {currentBooking.origin} → {currentBooking.destination}
              </div>
              <div className="text-xs text-gray-600">
                {formatDateTime(currentBooking.dep_date_time)}
              </div>
              <div className="text-xs text-gray-500">
                {[currentBooking.airline_code, currentBooking.airline_flight_no]
                  .filter(Boolean)
                  .join(" ") || "—"}
              </div>
              <div className="text-sm font-bold text-gray-700">
                {formatINR(currentBooking.totalfare)}
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-400">PNR: {ticket.pnr || "—"}</div>
          )}
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-2">
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wide">
            New flight the customer wants
          </div>
          <div className="text-sm font-semibold">
            {details.new_origin || "—"} → {details.new_destination || "—"}
          </div>
          <div className="text-xs text-gray-600">
            {formatDateTime(details.new_departure)}
          </div>
          <div className="text-xs text-gray-500">
            {details.new_airline || ""}{" "}
            {details.new_flight_number || ""}
          </div>
        </div>
      </div>

      {details.reschedule_reason && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
          <span className="font-semibold">Why the customer wants to change: </span>
          {details.reschedule_reason}
        </div>
      )}
    </div>
  );
}

// What the "Check with the airline" answer means, in plain words.
export function plainCheckResult(r) {
  switch (r?.status) {
    case "reissued":
      return r.settled === false
        ? { tone: "red", text: "The airline HAS changed the flight, but saving it here failed. Do not change it again. Ask a senior agent to fix the booking." }
        : { tone: "green", text: "Good news: the airline had already changed the flight. The booking is updated and the customer has been told." };
    case "not_reissued":
      return { tone: "amber", text: "The airline has NOT changed the flight. It is safe to try again." };
    case "retried_reissued":
      return { tone: "green", text: "Done: the flight has now been changed." };
    case "retry_failed":
      return { tone: "red", text: "We tried again but the change did not go through. Read the latest activity below, or ask a senior agent." };
    case "unknown":
      return { tone: "red", text: "The airline still did not give a clear answer. Wait a few minutes and check again. Do not change the flight yourself." };
    case "busy":
      return { tone: "blue", text: "Someone is already checking or changing this flight. Wait a minute and refresh." };
    case "already_reissued":
      return { tone: "green", text: "This flight was already changed." };
    case "not_unverified":
      return { tone: "gray", text: "There is nothing to check on this ticket." };
    default:
      return { tone: "red", text: "We could not reach the airline right now. Try again in a few minutes." };
  }
}

function AirlineCheckCard({ unclear, running, result, loading, onCheck, onRetry }) {
  const answer = result ? plainCheckResult(result) : null;
  const box = {
    green: "border-green-300 bg-green-50 text-green-900",
    amber: "border-amber-300 bg-amber-50 text-amber-900",
    red: "border-red-300 bg-red-50 text-red-900",
    blue: "border-blue-300 bg-blue-50 text-blue-900",
    gray: "border-gray-300 bg-gray-50 text-gray-800",
  };
  return (
    <div className="space-y-2" data-testid="airline-check">
      {unclear ? (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          <div className="font-semibold">We are not sure if the flight was changed</div>
          <p className="mt-0.5 text-xs">
            We asked the airline to change this flight but did not get a clear answer. Do not change
            it again by hand, or the customer could be changed (and charged) twice. Check with the
            airline first.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCheck}
              disabled={!!loading}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            >
              {loading === "check" ? "Checking…" : "Check with the airline"}
            </button>
            {result?.status === "not_reissued" ? (
              <button
                type="button"
                onClick={onRetry}
                disabled={!!loading}
                className="rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-60"
              >
                {loading === "retry" ? "Trying again…" : "Try the change again"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {running && !unclear ? (
        <div className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <div className="font-semibold">The flight is being changed right now</div>
          <p className="mt-0.5 text-xs">Please wait. This page updates on its own when it is done.</p>
        </div>
      ) : null}
      {answer ? (
        <div className={`rounded-xl border px-4 py-2.5 text-sm ${box[answer.tone]}`} data-testid="airline-check-answer">
          {answer.text}
        </div>
      ) : null}
    </div>
  );
}

// ─── Offline reissue ──────────────────────────────────────────────────────────
// TBO refuses an online change for this booking (server:
// modules/reschedule/offlineReissue.service.js, reschedule_details.handling_mode).
// The agent sends up to 3 flight options to the chat, the customer picks one, the
// agent saves the fee with its reference, and Apply sends the change to TBO offline.

export function isOfflineReissueTicket(ticket) {
  return ticket?.reschedule_details_parsed?.handling_mode === "offline_reissue";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-12-05T23:10" -> "5 Dec 2026, 23:10"; "2026-12-05" -> "5 Dec 2026" (as typed, no timezone shift).
function readableLocal(v) {
  const m = String(v ?? "").match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return String(v ?? "");
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}${m[4] ? `, ${m[4]}:${m[5]}` : ""}`;
}

export function flightOptionText(o, formatINR) {
  if (!o) return "";
  return (
    `${o.flight_number}${o.airline ? ` (${o.airline})` : ""}, ${o.origin} → ${o.destination}, ` +
    `departs ${readableLocal(o.departure)}` +
    (o.arrival ? `, arrives ${readableLocal(o.arrival)}` : "") +
    `, total ${formatINR(o.total_charge)}`
  );
}

// "UL226" / "UL 226" -> { code: "UL", number: "226" }
function splitFlightNumber(v) {
  const m = String(v ?? "").trim().toUpperCase().match(/^([A-Z0-9]{2})\s?(\d{1,5})$/);
  return m ? { code: m[1], number: m[2] } : { code: "", number: String(v ?? "").trim() };
}

function OfflineReissueBanner({ ticket, formatINR }) {
  const d = ticket?.reschedule_details_parsed || {};
  const off = d.offline_reissue || {};
  const dates = (off.preferred_dates || []).filter(Boolean);
  const fee = d.fee_entry;
  const actual = d.tbo_actual_charge;
  const diff = fee && actual?.amount != null ? Number(actual.amount) - Number(fee.amount) : null;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1" data-testid="offline-reissue-banner">
      <div className="font-semibold">Online change not allowed by TBO. Handle this one offline.</div>
      <p className="text-xs">
        TBO said: "{off.tbo_message || "Auto Reissuance not allowed"}"
        {off.airline_name || off.airline_code ? ` (${[off.airline_name, off.airline_code].filter(Boolean).join(", ")})` : ""}.
      </p>
      <p className="text-xs">
        Customer's preferred date:{" "}
        <strong>{dates.length ? dates.map(readableLocal).join(", return ") : "not given"}</strong>
      </p>
      {d.agreed_option ? (
        <p className="text-xs" data-testid="offline-agreed-flight">
          Customer chose: <strong>{flightOptionText(d.agreed_option, formatINR)}</strong>
        </p>
      ) : null}
      {fee ? (
        <p className="text-xs" data-testid="offline-fee-entry">
          Fee entered: <strong>{formatINR(fee.amount)}</strong>
          {fee.reference ? ` (reference: ${fee.reference})` : ""}, by {fee.entered_by || "—"} on {formatDateTime(fee.entered_at)}
          {fee.change_reason ? `. Changed from the option's ${formatINR(fee.option_total)}. Shown to the customer: ${fee.change_reason}` : ""}
          {fee.change_note ? `. Internal note: ${fee.change_note}` : ""}
        </p>
      ) : null}
      {actual ? (
        <p className="text-xs" data-testid="tbo-actual-charge">
          TBO's actual charge:{" "}
          <strong>{actual.amount != null ? formatINR(actual.amount) : "not shown by TBO"}</strong>
          {actual.invoice_no ? ` (invoice ${actual.invoice_no})` : ""}
          {diff != null
            ? Math.abs(diff) < 0.01
              ? `, same as the fee entered (${formatINR(fee.amount)})`
              : `, ${formatINR(Math.abs(diff))} ${diff > 0 ? "more" : "less"} than the fee entered (${formatINR(fee.amount)})`
            : ""}
        </p>
      ) : null}
    </div>
  );
}

// Options whose airports differ from the booking (a warning; the agent may mean it).
function optionRouteWarning(row, d) {
  const from = String(d?.original_origin || "").toUpperCase();
  const to = String(d?.original_destination || "").toUpperCase();
  const o = String(row.origin || "").toUpperCase();
  const t = String(row.destination || "").toUpperCase();
  if (o.length !== 3 || t.length !== 3 || (!from && !to)) return null;
  if ((from && o !== from) || (to && t !== to)) return `This option is ${o} → ${t}, but the booking is ${from || "—"} → ${to || "—"}.`;
  return null;
}

const EMPTY_OPTION = { flight_number: "", airline: "", origin: "", destination: "", departure: "", arrival: "", total_charge: "" };

function OfflineOptionsCard({ ticket, ticket_id, formatINR, onTicketRefresh }) {
  const d = ticket?.reschedule_details_parsed || {};
  const offered = d.flight_options;
  const preferred = (d.offline_reissue?.preferred_dates || [])[0] || d.preferred_date || "";
  const blankRow = () => ({
    ...EMPTY_OPTION,
    origin: d.original_origin || "",
    destination: d.original_destination || "",
    departure: preferred ? `${String(preferred).slice(0, 10)}T` : "",
  });
  const [rows, setRows] = useState(() =>
    offered?.options?.length
      ? offered.options.map((o) => ({ ...EMPTY_OPTION, ...o, airline: o.airline || "", arrival: o.arrival || "", total_charge: String(o.total_charge) }))
      : [blankRow()]
  );
  const [sending, setSending] = useState(false);
  const locked =
    ticket?.status === "Closed" ||
    isReschedulePaid(ticket) ||
    Boolean(ticket?.reschedule_payment_link_id) ||
    d.manual_reissue?.status === "pending";

  const setField = (i, key, value) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));

  const handleSend = async () => {
    if (d.agreed_option && !window.confirm("The customer already chose a flight. Sending new options clears that choice and the fee saved for it. Continue?")) return;
    const warnings = rows.map((r) => optionRouteWarning(r, d)).filter(Boolean);
    if (warnings.length && !window.confirm(`${warnings.join("\n")}\n\nSend these options anyway?`)) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/tickets/${ticket_id}/reschedule-options`, {
        options: rows.map((r) => ({ ...r, total_charge: Number(r.total_charge) })),
      });
      if (res.data?.data?.delivered === false) toast.error(res.data?.message || "Saved, but the chat could not be updated.");
      else toast.success(res.data?.message || "Options sent to the customer");
      onTicketRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not send the options. Try again.");
    } finally {
      setSending(false);
    }
  };

  const input = "w-full border rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60";
  return (
    <div className="space-y-3" data-testid="offline-options">
      <p className="text-sm text-gray-600">
        Get flight options for the customer's date from TBO (offline) or the airline. Send up to 3;
        the customer picks one in the chat. The total is what the customer pays for the change.
      </p>

      {offered?.options?.length ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1">
          <div className="font-semibold text-gray-600">
            Sent {formatDateTime(offered.sent_at)} by {offered.sent_by || "—"}
          </div>
          {offered.options.map((o) => (
            <div key={o.number}>
              Option {o.number}: {flightOptionText(o, formatINR)}
            </div>
          ))}
          <div className="pt-1 font-semibold">
            {d.agreed_option
              ? `Customer chose option ${d.agreed_option.number}.`
              : d.options_declined_at
                ? "Customer said none of these suit them. Find other options and send them again."
                : "Waiting for the customer to choose in the chat."}
          </div>
        </div>
      ) : null}

      {!locked ? (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600">Option {i + 1}</span>
                {rows.length > 1 ? (
                  <button type="button" className="text-xs text-red-600" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                ) : null}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <input className={input} placeholder="Flight, e.g. UL 226" value={r.flight_number} onChange={(e) => setField(i, "flight_number", e.target.value.toUpperCase())} disabled={sending} />
                <input className={input} placeholder="Airline (optional)" value={r.airline} onChange={(e) => setField(i, "airline", e.target.value)} disabled={sending} />
                <input className={input} placeholder="From, e.g. DXB" maxLength={3} value={r.origin} onChange={(e) => setField(i, "origin", e.target.value.toUpperCase())} disabled={sending} />
                <input className={input} placeholder="To, e.g. MAA" maxLength={3} value={r.destination} onChange={(e) => setField(i, "destination", e.target.value.toUpperCase())} disabled={sending} />
              </div>
              {optionRouteWarning(r, d) ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-1 text-xs text-amber-800" data-testid="option-route-warning">
                  {optionRouteWarning(r, d)}
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="text-xs text-gray-500">
                  Departs (local time)
                  <input type="datetime-local" className={input} value={r.departure} onChange={(e) => setField(i, "departure", e.target.value)} disabled={sending} />
                </label>
                <label className="text-xs text-gray-500">
                  Arrives (optional)
                  <input type="datetime-local" className={input} value={r.arrival} onChange={(e) => setField(i, "arrival", e.target.value)} disabled={sending} />
                </label>
                <label className="text-xs text-gray-500">
                  Total charge (₹)
                  <input type="number" min="1" className={input} placeholder="e.g. 12500" value={r.total_charge} onChange={(e) => setField(i, "total_charge", e.target.value)} disabled={sending} />
                </label>
              </div>
            </div>
          ))}
          {rows.length < 3 ? (
            <button type="button" className="text-sm text-blue-600 font-semibold" onClick={() => setRows((rs) => [...rs, blankRow()])} disabled={sending}>
              + Add another option
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="w-full rounded-xl bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {sending ? "Sending…" : offered?.options?.length ? "Send new options (replaces the earlier ones)" : "Send options to the customer"}
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-500">The options can't be changed now: the payment link was already sent for the agreed flight.</p>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ReschedulingPanel({
  ticket,
  ticket_id,
  booking,
  bookings,
  formatINR: formatINRProp,
  onTicketRefresh,
}) {
  const formatINR = formatINRProp || defaultFormatINR;
  const activeStep = deriveActiveStep(ticket);
  const offline = isOfflineReissueTicket(ticket);
  const agreedOption = ticket?.reschedule_details_parsed?.agreed_option || null;

  // Step 2 — TBO charges
  const [tboCharges, setTboCharges] = useState(null);
  const [tboLoading, setTboLoading] = useState(false);
  const [feeInput, setFeeInput] = useState(
    ticket?.reschedule_fee != null ? String(Number(ticket.reschedule_fee)) : ""
  );
  const [feeSubmitting, setFeeSubmitting] = useState(false);
  // Offline reissue: where the fee came from (TBO / airline quote), required.
  const [feeReference, setFeeReference] = useState(ticket?.reschedule_details_parsed?.fee_entry?.reference || "");
  // Offline reissue: why the fee differs from the chosen option's total (told to the customer).
  const [feeChangeReason, setFeeChangeReason] = useState(ticket?.reschedule_details_parsed?.fee_entry?.change_reason || "");
  // Offline reissue: agents-only note on the fee change (never sent to the customer).
  const [feeChangeNote, setFeeChangeNote] = useState(ticket?.reschedule_details_parsed?.fee_entry?.change_note || "");
  // Offline reissue: Step 1's options form again after the customer chose (until a link is sent).
  const [showChangeOptions, setShowChangeOptions] = useState(false);
  const [feeConfirmed, setFeeConfirmed] = useState(
    Number(ticket?.tbo_verified) === 1 ||
    (ticket?.reschedule_fee != null && String(ticket.reschedule_fee).trim() !== "")
  );

  // Offline reissue: the fee starts as the chosen option's total. A fee cleared on the
  // server (new options sent) opens the fee entry again.
  const optionTotal = offline && agreedOption ? Number(agreedOption.total_charge) : null;
  const feeDiffers =
    optionTotal != null && feeInput !== "" && Number.isFinite(Number(feeInput)) && Math.abs(Number(feeInput) - optionTotal) > 0.005;
  useEffect(() => {
    if (!offline) return;
    const feeSaved = ticket?.reschedule_fee != null && String(ticket.reschedule_fee).trim() !== "";
    if (feeSaved) return;
    setFeeConfirmed(false);
    setFeeInput(agreedOption ? String(agreedOption.total_charge) : "");
    setFeeChangeReason("");
    setFeeChangeNote("");
  }, [offline, agreedOption?.agreed_at, ticket?.reschedule_fee]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 3 — notify fee
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(Number(ticket?.tbo_verified) === 1);

  // Step 4 — payment link
  const [payLinkLoading, setPayLinkLoading] = useState(false);
  const [payLinkCreated, setPayLinkCreated] = useState(!!ticket?.reschedule_payment_link_id);
  const [payLinkUrl, setPayLinkUrl] = useState(ticket?.reschedule_payment_link_url || "");

  // Step 5 — wait for payment
  const [refreshingPayment, setRefreshingPayment] = useState(false);

  // Polling fallback — poll Razorpay directly every 8s while waiting for payment
  const pollRef = useRef(null);
  useEffect(() => {
    if (activeStep !== 5 || !onTicketRefresh) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.post(`/admin/tickets/${ticket_id}/sync-reschedule-payment`, {});
        if (res.data?.changed || res.data?.status === "paid") {
          await onTicketRefresh();
        }
      } catch { /* non-blocking */ }
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [activeStep, onTicketRefresh, ticket_id]);

  // Step 6 — apply reschedule
  const details = ticket?.reschedule_details_parsed;
  const { code: parsedCode, number: parsedNumber } = parseFlightToken(details?.new_flight_number);

  const [rescheduleForm, setRescheduleForm] = useState({
    new_pnr: "",
    // new_departure (actual TBO time) first; preferred_date (chatbot date picker) as fallback
    // Offline reissue: the flight the customer agreed in the chat, as typed (local time).
    new_flight_date:
      (details?.agreed_option?.departure ? String(details.agreed_option.departure).slice(0, 16) : "") ||
      toDateTimeLocal(details?.new_departure) || toDateTimeLocal(details?.preferred_date) || "",
    new_airline_code:
      splitFlightNumber(details?.agreed_option?.flight_number).code ||
      details?.new_airline_code || parsedCode || booking?.airline_code || "",
    new_airline_flight_no:
      (details?.agreed_option ? splitFlightNumber(details.agreed_option.flight_number).number : "") ||
      parsedNumber || booking?.airline_flight_no || "",
    new_flight_id: "",
    remarks: details?.reschedule_reason || "",
  });
  const [applySubmitting, setApplySubmitting] = useState(false);

  // "Check with the airline" (a change sent to TBO with no clear answer)
  const [checkLoading, setCheckLoading] = useState("");
  const [checkResult, setCheckResult] = useState(null);

  // Sync form from ticket/booking when details arrive (e.g. after ticket refresh).
  // Only fills empty fields so admin manual edits are preserved.
  useEffect(() => {
    const d = ticket?.reschedule_details_parsed;
    const { code: dCode, number: dNumber } = parseFlightToken(d?.new_flight_number);
    const agreed = d?.agreed_option;
    const agreedFlight = agreed ? splitFlightNumber(agreed.flight_number) : null;
    setRescheduleForm((prev) => ({
      ...prev,
      new_flight_date:
        prev.new_flight_date ||
        (agreed?.departure ? String(agreed.departure).slice(0, 16) : "") ||
        toDateTimeLocal(d?.new_departure) ||
        toDateTimeLocal(d?.preferred_date) ||
        "",
      new_airline_code:
        prev.new_airline_code ||
        agreedFlight?.code ||
        d?.new_airline_code ||
        dCode ||
        booking?.airline_code ||
        "",
      new_airline_flight_no:
        prev.new_airline_flight_no ||
        agreedFlight?.number ||
        dNumber ||
        booking?.airline_flight_no ||
        "",
      remarks:
        prev.remarks || d?.reschedule_reason || "",
    }));
  }, [ticket, booking]);

  const handleGetTboCharges = async () => {
    setTboLoading(true);
    try {
      // Reschedule (reissue) quote for the flight the customer picked: change fee,
      // price difference and the total the customer pays (server:
      // getTicketRescheduleCharges). Not the cancellation charges.
      const res = await api.post(`/admin/tickets/${ticket_id}/reschedule-charges`, {});
      const data = res.data?.data;
      setTboCharges(data);
      const total = data?.total_payable;
      if (total != null && Number.isFinite(Number(total)) && !feeInput) {
        setFeeInput(String(Math.round(Number(total) * 100) / 100));
      }
      toast.success("Got the fee from the airline");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not get the fee from the airline. Try again.");
    } finally {
      setTboLoading(false);
    }
  };

  const handleConfirmFee = async () => {
    const fee = Number(feeInput);
    if (!Number.isFinite(fee) || fee < 0) {
      toast.error("Enter the fee as a number, for example 3500");
      return;
    }
    const reference = feeReference.trim();
    if (offline) {
      if (fee <= 0) {
        toast.error("Enter the fee the customer pays (more than ₹0)");
        return;
      }
      if (reference.length < 3) {
        toast.error("Enter where the fee came from, for example the TBO quote reference");
        return;
      }
      if (feeDiffers && feeChangeReason.trim().length < 3) {
        toast.error("The fee differs from the chosen option's total. Enter the reason shown to the customer.");
        return;
      }
    }
    setFeeSubmitting(true);
    try {
      await api.post("/admin/tickets/update-reschedule-status", {
        ticket_id,
        reschedule_fee: fee,
        ...(reference ? { fee_reference: reference } : {}),
        ...(offline && feeDiffers
          ? { fee_change_reason: feeChangeReason.trim(), ...(feeChangeNote.trim() ? { fee_change_note: feeChangeNote.trim() } : {}) }
          : {}),
      });
      setFeeConfirmed(true);
      toast.success("Fee saved");
      onTicketRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not save the fee. Try again.");
    } finally {
      setFeeSubmitting(false);
    }
  };

  const handleNotifyFee = async () => {
    // Offline reissue: always the saved fee (the server refuses any other amount).
    const fee = offline ? Number(ticket?.reschedule_fee) : Number(feeInput || ticket?.reschedule_fee);
    if (!Number.isFinite(fee) || fee < 0 || (offline && fee <= 0)) {
      toast.error("Save the fee in step 2 first.");
      return;
    }
    setNotifySubmitting(true);
    try {
      await api.post(`/admin/tickets/${ticket_id}/notify-reschedule-fee`, {
        reschedule_fee: fee,
      });
      setNotifyDone(true);
      toast.success("The customer has been told the fee");
      onTicketRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not send the fee to the customer. Try again.");
    } finally {
      setNotifySubmitting(false);
    }
  };

  const handleCreatePaymentLink = async () => {
    setPayLinkLoading(true);
    try {
      const res = await api.post(`/admin/tickets/${ticket_id}/create-reschedule-payment-link`, {});
      const url = res.data?.data?.payment_link_url;
      setPayLinkUrl(url || "");
      setPayLinkCreated(true);
      toast.success("Payment link sent to the customer");
      onTicketRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not send the payment link. Try again.");
    } finally {
      setPayLinkLoading(false);
    }
  };

  const handleRefreshPaymentStatus = async () => {
    setRefreshingPayment(true);
    try {
      // Poll Razorpay directly — don't rely on webhook delivery
      const res = await api.post(`/admin/tickets/${ticket_id}/sync-reschedule-payment`, {});
      if (res.data?.changed) {
        toast.success("The customer has paid. The page will update now.");
      } else if (res.data?.status === "paid") {
        toast.success("The customer has already paid");
      } else {
        toast("The customer has not paid yet", { icon: "⏳" });
      }
      await onTicketRefresh?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Could not check the payment. Try again.";
      toast.error(msg);
    } finally {
      setRefreshingPayment(false);
    }
  };

  const handleApplyReschedule = async () => {
    // PNR is optional: TBO normally keeps the same PNR on a reschedule. Leave it
    // blank (or enter the current PNR) and the booking keeps its PNR.
    const pnrInput = rescheduleForm.new_pnr.trim().toUpperCase();
    if (pnrInput && !/^[A-Z0-9]{6,8}$/.test(pnrInput)) {
      toast.error("The PNR should be 6 to 8 letters or numbers");
      return;
    }
    if (!rescheduleForm.new_flight_date) {
      toast.error("Enter the new departure date and time");
      return;
    }
    setApplySubmitting(true);
    try {
      const res = await api.post(`/admin/tickets/${ticket_id}/reschedule`, {
        ...(pnrInput ? { new_pnr: pnrInput } : {}),
        new_flight_date: rescheduleForm.new_flight_date,
        new_airline_code: rescheduleForm.new_airline_code || undefined,
        new_airline_flight_no: rescheduleForm.new_airline_flight_no || undefined,
        // What the customer paid for the change (never negative: a reschedule
        // gives no money back).
        fare_difference: currentFee,
        new_flight_id: rescheduleForm.new_flight_id || undefined,
        remarks: rescheduleForm.remarks || undefined,
      });
      const tboId = res.data?.data?.tbo_change_request_id;
      // pending_tbo: the request is with TBO; nothing changes for the customer until
      // TBO completes it. Applying again checks TBO.
      if (res.data?.data?.status === "pending_tbo") {
        toast.success(
          tboId
            ? `Sent to TBO (request number ${tboId}). The flight changes once TBO completes it.`
            : res.data?.message || "TBO has not completed the reissue yet. Nothing was changed."
        );
      } else {
        toast.success("Flight changed. The customer has been told.");
      }
      onTicketRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not change the flight. Try again or ask a senior agent.");
    } finally {
      setApplySubmitting(false);
    }
  };

  // A change already sent to TBO that is still running or has no clear answer:
  // the flight must not be changed again until it is checked.
  const reissueMarker = String(ticket?.reissued_tbo_booking_id || "");
  const changeUnclear = reissueMarker === "UNVERIFIED";
  const changeRunning = reissueMarker === "REISSUING" || reissueMarker === "VERIFYING";
  const changeBlocked = changeUnclear || changeRunning;
  const feeReturned = String(ticket?.reschedule_payment_status || "").toLowerCase() === "reversed";

  const handleCheckWithAirline = async (action) => {
    if (
      action === "retry" &&
      !window.confirm(
        "Try the change again?\n\nOnly do this because the check said the flight was NOT changed. The system checks with the airline once more before it tries."
      )
    ) {
      return;
    }
    setCheckLoading(action);
    try {
      const res = await api.post(`/admin/tickets/${ticket_id}/verify-reissue`, { action });
      setCheckResult(res.data);
      await onTicketRefresh?.();
    } catch (err) {
      const data = err?.response?.data;
      setCheckResult(data || { status: "error" });
      toast.error(plainCheckResult(data || { status: "error" }).text);
    } finally {
      setCheckLoading("");
    }
  };

  const currentFee =
    Number(ticket?.reschedule_fee) ||
    Number(ticket?.reschedule_payment_amount) ||
    Number(feeInput) ||
    0;

  // The chat's automatic reschedule owns this ticket while it runs (server:
  // openAutomationTicket). It closes on its own, or comes back here if it fails.
  const automationStatus = ticket?.reschedule_details_parsed?.automation?.status || null;
  if (automationStatus === "running") {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="automation-running">
          <div className="font-semibold">The customer's reschedule is being done automatically</div>
          <p className="mt-0.5 text-xs">
            No action needed. The customer was quoted
            {ticket?.reschedule_details_parsed?.automation?.quoted_total != null
              ? ` ${formatINR(ticket.reschedule_details_parsed.automation.quoted_total)}`
              : " the airline's fee"}{" "}
            and pays through the chat. This ticket closes on its own when the flight is changed, or
            comes back to you here if anything fails.
          </p>
        </div>
        <TripLegsList ticket={ticket} />
        <FlightComparisonCard ticket={ticket} bookings={bookings} formatINR={formatINR} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {changeUnclear || changeRunning || checkResult ? (
        <AirlineCheckCard
          unclear={changeUnclear}
          running={changeRunning}
          result={checkResult}
          loading={checkLoading}
          onCheck={() => handleCheckWithAirline("check")}
          onRetry={() => handleCheckWithAirline("retry")}
        />
      ) : null}

      {feeReturned ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="fee-returned">
          <div className="font-semibold">The flight was not changed, and the fee was given back</div>
          <p className="mt-0.5 text-xs">
            The airline could not make the change, so {formatINR(ticket?.reschedule_fee)} went back
            to the customer's credit line. Call the customer and help them pick another flight if
            they still want to change.
          </p>
        </div>
      ) : null}

      {offline ? <OfflineReissueBanner ticket={ticket} formatINR={formatINR} /> : null}

      <StepTracker activeStep={activeStep} />

      {/* Step 1 — Request overview */}
      <StepCard
        stepNum={1}
        activeStep={activeStep}
        title={
          offline
            ? agreedOption
              ? `Step 1: Flight agreed with the customer (${agreedOption.flight_number})`
              : "Step 1: Agree a flight with the customer"
            : "Step 1: Check the new flight the customer wants"
        }
        icon="📋"
      >
        {offline ? (
          <OfflineOptionsCard ticket={ticket} ticket_id={ticket_id} formatINR={formatINR} onTicketRefresh={onTicketRefresh} />
        ) : ticket?.reschedule_details_parsed ? (
          <>
            <TripLegsList ticket={ticket} />
            <FlightComparisonCard ticket={ticket} bookings={bookings} formatINR={formatINR} />
          </>
        ) : (
          <p className="text-sm text-gray-500">
            The customer has not picked a new flight in the app yet. It will show here as soon as
            they do.
          </p>
        )}
      </StepCard>

      {offline &&
      agreedOption &&
      !ticket?.reschedule_payment_link_id &&
      !isReschedulePaid(ticket) &&
      ticket?.reschedule_details_parsed?.manual_reissue?.status !== "pending" &&
      ticket?.status !== "Closed" ? (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 space-y-3" data-testid="change-options">
          <button
            type="button"
            onClick={() => setShowChangeOptions((v) => !v)}
            className="w-full rounded-xl border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            {showChangeOptions ? "Keep the agreed flight" : "Change options"}
          </button>
          {showChangeOptions ? (
            <OfflineOptionsCard ticket={ticket} ticket_id={ticket_id} formatINR={formatINR} onTicketRefresh={onTicketRefresh} />
          ) : null}
        </div>
      ) : null}

      {/* Step 2 — Get fee */}
      <StepCard
        stepNum={2}
        activeStep={activeStep}
        title={offline ? "Step 2: Save the fee for the chosen flight" : "Step 2: Get the change fee from the airline"}
        icon="💰"
      >
        {offline ? (
          <div className="space-y-1">
            <p className="text-sm text-gray-600">
              TBO can't quote this change online. Get the change fee and fare difference for the
              flight the customer chose from TBO (offline) or the airline, then save the total the
              customer pays and where it came from.
            </p>
            {agreedOption ? (
              <p className="text-xs text-gray-700">
                For: <strong>{flightOptionText(agreedOption, formatINR)}</strong>
              </p>
            ) : null}
          </div>
        ) : (
        <>
        <p className="text-sm text-gray-600">
          Click the button to ask the airline how much it charges to change this flight. Check the
          amount, change it if needed, then save it.
        </p>

        <button
          type="button"
          onClick={handleGetTboCharges}
          disabled={tboLoading}
          className="w-full rounded-xl bg-gray-800 text-white px-4 py-2.5 text-sm font-semibold hover:bg-gray-900 disabled:opacity-60"
        >
          {tboLoading ? "Asking the airline…" : "Get the fee from the airline"}
        </button>

        {tboCharges?.charges ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1" data-testid="reissue-quote">
            <div className="font-semibold text-gray-600 mb-1">
              What the airline charges to change to{" "}
              {[tboCharges.flight?.flight_number, tboCharges.flight?.origin && tboCharges.flight?.destination
                ? `${tboCharges.flight.origin} → ${tboCharges.flight.destination}` : null].filter(Boolean).join(", ") || "the new flight"}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Airline change fee</span>
              <span className="font-semibold">{tboCharges.charges.airline_change_fee != null ? formatINR(tboCharges.charges.airline_change_fee) : "—"}</span>
            </div>
            {tboCharges.charges.fare_difference != null && tboCharges.charges.fare_difference < 0 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fare difference</span>
                  <span className="font-semibold">New flight is cheaper: nothing is refunded</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Published fare (charged when the new flight is cheaper)</span>
                  <span className="font-semibold">{tboCharges.charges.published_fare != null ? formatINR(tboCharges.charges.published_fare) : "—"}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between">
                <span className="text-gray-500">Fare difference</span>
                <span className="font-semibold">{tboCharges.charges.fare_difference != null ? formatINR(tboCharges.charges.fare_difference) : "—"}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-gray-200 pt-1">
              <span className="font-semibold text-gray-700">Customer pays in total</span>
              <span className="font-bold">{tboCharges.charges.total_payable != null ? formatINR(tboCharges.charges.total_payable) : "—"}</span>
            </div>
            {tboCharges.charges.is_price_changed ? (
              <div className="text-amber-700">The airline changed the price since the customer picked this flight.</div>
            ) : null}
          </div>
        ) : null}

        {ticket?.reschedule_charges_estimate != null && !tboCharges && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
            <span className="font-semibold">Expected fee, from the fare rules: </span>
            {String(ticket.reschedule_charges_estimate)}
          </div>
        )}
        </>
        )}

        <div className="border-t border-gray-100 pt-3 space-y-2">
          <label className="text-sm font-semibold text-gray-700 block">
            Change fee to charge the customer (₹)
          </label>
          <input
            type="number"
            min="0"
            placeholder="e.g. 3500"
            value={feeInput}
            onChange={(e) => setFeeInput(e.target.value)}
            disabled={feeConfirmed}
            className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:bg-gray-50"
          />
          {offline ? (
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1">
                Reference for this fee <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={120}
                placeholder="e.g. TBO offline quote ref / email subject"
                value={feeReference}
                onChange={(e) => setFeeReference(e.target.value)}
                disabled={feeConfirmed}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:bg-gray-50"
              />
              {optionTotal != null ? (
                <p className="mt-1 text-xs text-gray-500">The chosen option's total was {formatINR(optionTotal)} (filled in above).</p>
              ) : null}
            </div>
          ) : null}
          {offline && feeDiffers ? (
            <div data-testid="fee-change-reason">
              <label className="text-sm font-semibold text-gray-700 block mb-1">
                Shown to the customer: why the fee is different from {formatINR(optionTotal)} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={200}
                placeholder="e.g. The airline's fare went up before payment"
                value={feeChangeReason}
                onChange={(e) => setFeeChangeReason(e.target.value)}
                disabled={feeConfirmed}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:bg-gray-50"
              />
              <p className="mt-1 text-xs text-gray-500">The customer sees this with the fee in the chat.</p>
              <label className="text-sm font-semibold text-gray-700 block mb-1 mt-3">
                Internal note <span className="font-normal text-gray-400">(agents only, optional)</span>
              </label>
              <textarea
                rows={2}
                maxLength={500}
                placeholder="e.g. TBO desk quoted the new fare on call ref 1234"
                value={feeChangeNote}
                onChange={(e) => setFeeChangeNote(e.target.value)}
                disabled={feeConfirmed}
                data-testid="fee-change-note"
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:bg-gray-50"
              />
              <p className="mt-1 text-xs text-gray-500">Saved in the ticket history. Never sent to the customer.</p>
            </div>
          ) : null}
          {feeInput && Number(feeInput) >= 0 && (
            <div className="rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-800">
              The customer will pay{" "}
              <strong>{formatINR(Number(feeInput))}</strong> to change the flight.
            </div>
          )}
          {!feeConfirmed ? (
            <button
              type="button"
              onClick={handleConfirmFee}
              disabled={feeSubmitting || !feeInput || Number(feeInput) < 0 || (offline && (Number(feeInput) <= 0 || feeReference.trim().length < 3 || (feeDiffers && feeChangeReason.trim().length < 3)))}
              className="w-full rounded-xl bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {feeSubmitting ? "Saving…" : "Save this fee"}
            </button>
          ) : (
            <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-2.5 text-sm text-green-700 font-semibold">
              ✓ Fee saved: {formatINR(currentFee)}
            </div>
          )}
        </div>
      </StepCard>

      {/* Step 3 — Notify customer of fee */}
      <StepCard stepNum={3} activeStep={activeStep} title="Step 3: Tell the customer the fee" icon="📱">
        <p className="text-sm text-gray-600">
          The fee is <strong>{formatINR(currentFee)}</strong>. Send it to the customer's app so
          they know how much they will pay before you send the payment link.
        </p>

        {notifyDone ? (
          <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-2.5 text-sm text-green-700 font-semibold">
            ✓ The customer has been told the fee
          </div>
        ) : (
          <button
            type="button"
            onClick={handleNotifyFee}
            disabled={notifySubmitting}
            className="w-full rounded-xl bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {notifySubmitting ? "Sending…" : "Send the fee to the customer"}
          </button>
        )}
      </StepCard>

      {/* Step 4 — Send payment link */}
      <StepCard stepNum={4} activeStep={activeStep} title="Step 4: Send the payment link" icon="💳">
        <p className="text-sm text-gray-600">
          This sends the customer a link to pay <strong>{formatINR(currentFee)}</strong>. They get
          it as a notification in the app.
        </p>

        {payLinkCreated && payLinkUrl ? (
          <div className="space-y-2">
            <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-2.5 text-sm text-green-700 font-semibold">
              ✓ Payment link sent to the customer
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs break-all">
              <span className="text-gray-500 font-semibold">Link: </span>
              <a
                href={payLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                {payLinkUrl}
              </a>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleCreatePaymentLink}
            disabled={payLinkLoading}
            className="w-full rounded-xl bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {payLinkLoading ? "Sending…" : "Send the payment link"}
          </button>
        )}
      </StepCard>

      {/* Step 5 — Wait for payment */}
      <StepCard stepNum={5} activeStep={activeStep} title="Step 5: Wait for the customer to pay" icon="⏳">
        {isReschedulePaid(ticket) ? (
          <div className="space-y-2">
            <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 font-semibold">
              ✓ Payment received:{" "}
              {formatINR(ticket.reschedule_payment_amount || ticket.reschedule_fee)}
            </div>
            {ticket.reschedule_paid_at && (
              <div className="text-xs text-gray-500">
                Paid on: {formatDateTime(ticket.reschedule_paid_at)}
              </div>
            )}
            {ticket.reschedule_razorpay_payment_id && (
              <div className="text-xs text-gray-500 font-mono">
                Payment reference: {ticket.reschedule_razorpay_payment_id}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
              <div className="font-semibold mb-0.5">Waiting for the customer to pay</div>
              <div className="text-xs">
                The customer has a link to pay{" "}
                <strong>
                  {formatINR(ticket?.reschedule_payment_amount || ticket?.reschedule_fee)}
                </strong>
                . This page checks for the payment every few seconds, so you do not need to do
                anything.
              </div>
            </div>
            {ticket?.reschedule_payment_link_url && (
              <div className="text-xs text-gray-500">
                Link:{" "}
                <a
                  href={ticket.reschedule_payment_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {ticket.reschedule_payment_link_url}
                </a>
              </div>
            )}
            <button
              type="button"
              onClick={handleRefreshPaymentStatus}
              disabled={refreshingPayment}
              className="w-full rounded-xl border border-gray-300 bg-white text-gray-700 px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
            >
              {refreshingPayment ? "Checking…" : "Check for the payment now"}
            </button>
          </div>
        )}
      </StepCard>

      {/* Step 6 — Apply reschedule */}
      <StepCard
        stepNum={6}
        activeStep={activeStep}
        title="Step 6: Change the flight"
        icon="✈️"
      >
        {offline ? (
          <p className="text-sm text-gray-600">
            This sends the change to TBO offline. Check the details below (filled in from the flight
            the customer chose), then click the button. The booking changes only after TBO completes
            it; this page checks TBO for you, and the team is alerted if TBO hasn't confirmed in 48 hours.
          </p>
        ) : (
        <p className="text-sm text-gray-600">
          After payment the flight is usually changed automatically. Use this only if the ticket
          history says the automatic change failed. Check the details below (filled in from what
          the customer picked), then click the button.
        </p>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">
              Booking reference (PNR){" "}
              <span className="font-normal text-gray-400">
                (optional: leave empty if it did not change, which is normal)
              </span>
            </label>
            <input
              type="text"
              placeholder={ticket?.pnr ? `Current: ${ticket.pnr}` : "e.g. ABCXYZ"}
              value={rescheduleForm.new_pnr}
              onChange={(e) =>
                setRescheduleForm((f) => ({ ...f, new_pnr: e.target.value.toUpperCase() }))
              }
              maxLength={8}
              disabled={applySubmitting}
              className="w-full border rounded-xl px-3 py-2 text-sm font-mono font-semibold tracking-widest uppercase outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">
              New departure date and time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={rescheduleForm.new_flight_date}
              onChange={(e) =>
                setRescheduleForm((f) => ({ ...f, new_flight_date: e.target.value }))
              }
              disabled={applySubmitting}
              className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">
                Airline code <span className="text-gray-400 font-normal">(for example 6E)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. AI, 6E"
                value={rescheduleForm.new_airline_code}
                onChange={(e) =>
                  setRescheduleForm((f) => ({
                    ...f,
                    new_airline_code: e.target.value.toUpperCase(),
                  }))
                }
                maxLength={3}
                disabled={applySubmitting}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">
                Flight number <span className="text-gray-400 font-normal">(for example 101)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 101"
                value={rescheduleForm.new_airline_flight_no}
                onChange={(e) =>
                  setRescheduleForm((f) => ({
                    ...f,
                    new_airline_flight_no: e.target.value,
                  }))
                }
                disabled={applySubmitting}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">
              New TBO booking ID{" "}
              <span className="text-gray-400 font-normal">
                (optional: only if TBO already changed the ticket and gave you one)
              </span>
            </label>
            <input
              type="text"
              placeholder="Only if TBO gave you one"
              value={rescheduleForm.new_flight_id}
              onChange={(e) =>
                setRescheduleForm((f) => ({ ...f, new_flight_id: e.target.value }))
              }
              disabled={applySubmitting}
              className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">
              Note for other agents <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={2}
              placeholder="For example: customer asked for a morning flight"
              value={rescheduleForm.remarks}
              onChange={(e) =>
                setRescheduleForm((f) => ({ ...f, remarks: e.target.value }))
              }
              disabled={applySubmitting}
              className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 resize-none disabled:opacity-60"
            />
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
            When you click the button:
            <ul className="mt-1 ml-3 space-y-0.5 list-disc">
              <li>We send the change to TBO's team</li>
              <li>Nothing changes for the customer until TBO completes it</li>
              <li>We check with TBO every 15 minutes (click again to check now)</li>
              <li>Once TBO confirms: the booking shows the new flight, the customer is told, and this ticket closes</li>
            </ul>
          </div>

          <button
            type="button"
            onClick={handleApplyReschedule}
            disabled={
              applySubmitting ||
              !rescheduleForm.new_flight_date ||
              changeBlocked
            }
            className="w-full rounded-xl bg-blue-600 text-white px-4 py-3 text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {applySubmitting
              ? "Changing the flight…"
              : "Change the flight and finish"}
          </button>
          {changeBlocked ? (
            <p className="text-xs text-red-700">
              This button is off because a change was already sent to the airline. Use "Check
              with the airline" at the top first, so the customer is not changed twice.
            </p>
          ) : null}
        </div>
      </StepCard>

      {/* Closed without a change: the customer did not pay */}
      {activeStep >= 7 && automationStatus === "ended_unpaid" && (
        <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-4 text-sm text-gray-700" data-testid="automation-unpaid">
          <div className="font-bold">Closed: the customer did not pay</div>
          <div className="text-xs mt-0.5">The flight was not changed. Nothing else to do.</div>
        </div>
      )}

      {/* Done */}
      {activeStep >= 7 && automationStatus !== "ended_unpaid" && (
        <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-4 flex items-start gap-3">
          <span className="text-2xl leading-none">✅</span>
          <div>
            <div className="text-sm font-bold text-green-800">
              Flight changed
            </div>
            <div className="text-xs text-green-700 mt-0.5">
              The booking shows the new flight and the customer has been told. This ticket is
              closed.
            </div>
            {ticket?.tbo_change_request_id && (
              <div className="mt-2 text-xs text-green-800 font-mono bg-green-100 rounded-lg px-2.5 py-1.5 inline-block">
                TBO request number: <span className="font-bold">{ticket.tbo_change_request_id}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
